import { SxProps } from "@mui/material";
import { alpha, Theme } from "@mui/material/styles";

type StylesType = {
  [key: string]: SxProps<Theme>;
};

const createStyles = (theme: Theme): StylesType => ({
  // Table container styles - simplified to match platform standards
  tableContainer: {
    borderRadius: 1,
    overflow: "hidden",
    border: `1px solid ${theme.palette.divider}`,
  },

  // Loading state
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    py: 8,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 1,
    border: `1px solid ${theme.palette.divider}`,
  },

  // Header styles - simplified to match platform tables
  headerRow: {
    backgroundColor: theme.palette.grey[100],
  },

  tableHeaderCell: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    py: 1.5,
    px: 2,
    fontSize: "0.75rem",
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  headerCell: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    width: "100%",
    py: 0.5,
    transition: "color 0.15s ease",
    "&:hover": {
      color: theme.palette.primary.main,
    },
    "&.center-align": {
      justifyContent: "center",
    },
  },

  // Table body styles - simplified hover effects
  tableRow: {
    transition: "background-color 0.15s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },

  evenRow: {
    backgroundColor: theme.palette.grey[50],
  },

  highlightedRow: {
    backgroundColor: alpha(theme.palette.primary.light, 0.12),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    "& td:first-of-type": {
      pl: 1.5,
    },
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.light, 0.2),
    },
  },

  tableCell: {
    py: 1.5,
    px: 2,
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: "0.875rem",
  },

  // Cell content styles
  idBadge: {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    borderRadius: 1,
    py: 0.5,
    px: 1,
    minWidth: 40,
    textAlign: "center",
    display: "inline-block",
    fontSize: "0.8125rem",
    border: `1px solid ${theme.palette.grey[300]}`,
  },

  actionTypeName: {
    fontWeight: 500,
    fontSize: "0.875rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    py: 0.5,
  },

  centerContent: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },

  viewButton: {
    borderRadius: 1,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    py: 0.5,
    px: 1.5,
    fontWeight: 500,
    fontSize: "0.8125rem",
    textTransform: "none",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
      borderColor: theme.palette.primary.dark,
    },
  },

  createdByContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },

  createdByDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.info.main,
  },

  createdByText: {
    fontSize: "0.875rem",
    fontWeight: 400,
  },

  dateText: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },

  actionButton: {
    borderRadius: 1,
    py: 0.5,
    px: 1.5,
    fontWeight: 500,
    textTransform: "none",
    fontSize: "0.8125rem",
  },

  // Empty state styles
  noDataCell: {
    py: 5,
    px: 2,
    textAlign: "center",
  },

  noDataContainer: {
    textAlign: "center",
    py: 2,
  },

  noDataIconContainer: {
    width: 56,
    height: 56,
    borderRadius: "50%",
    backgroundColor: theme.palette.grey[100],
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    border: `1px dashed ${theme.palette.grey[400]}`,
  },

  // Pagination styles
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    py: 2,
    px: 2,
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
  },
});

export default createStyles;
